import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import { NumericInput } from '../../../../../components/shared/ui/NumericInput';
import { useAppDispatch, useAppSelector } from '../../../../../store';
import {
  updateBomData,
  setPackageItems,
  setBomType,
  resetBom,
  updateBillOfMaterialData,
  updateLgcyBomResults,
  setLgcyBomResultField,
  setNetsuiteItems,
} from '../../../../../store/slices/createCRSlice';
import {
  selectBomData,
  selectLgcyBomData,
  selectLenderPackageItems,
  selectClientNetsuiteItems,
  selectIsLgcyClient,
  selectTaskClient,
  selectBillOfMaterialData,
  selectBomTotalRowsOfPanels,
  selectBomStringsCircuits,
  selectBomMciS,
  selectBomTotalModules,
  selectBomIsEnphase,
  selectTotalInverters,
  selectTotalBatteries,
  selectTotalOptimizersRsd,
  selectTotalMicroinverters,
  selectAllSystemsModuleData,
  selectStructuresData,
  selectStructure1Data,
  selectStructure2Data,
  selectStructure3Data,
} from '../../../../../store/selectors/createCRSelectors';
import { selectLgcyBomOffset, selectSafetyFactor } from '../../../../../store/selectors/databaseSelectors';
import { ESS_CONFIG_OPTIONS, INSTALLATION_METHOD_OPTIONS, BomType, EssConfigValue, BOM_CSV_IMPORT } from '../../../../../constants/lender-packages.constants';
import { mapPackageItems, updatePackageItemQuantities, type PackageMapperInput } from '../../../../../utils/bom-package-mapper.utils';
import { SYSTEM_KEYS } from '../../../../../constants/aurora.constants';
import { lenderPackagesService } from '../../../../../services/lender-packages.service';
import {
  LenderResponse,
  LenderPackageResponse,
  PackageItemState,
  PackageLookupResult,
} from '../../../../../types/lender-packages.types';
import { SearchDropdown } from '../../../../../components/shared/ui/SearchDropdown';
import { ConfirmationModal, ConfirmationType } from '../../../../../components';
import { LenderPackagesTable } from './LenderPackagesTable';
import { ClientPackagesSection } from './ClientPackagesSection';
import { EnphaseItemsSection } from './EnphaseItemsSection';
import { useClientNetsuiteCatalog } from '../../../../../hooks/useClientNetsuiteCatalog';
import { useEnphaseNetsuiteSync } from '../../../../../hooks/useEnphaseNetsuiteSync';
import { useCrAutoSave } from '../../../../../hooks/useCrAutoSave';
import { CR_AUTO_SAVE } from '../../../../../constants/app.constants';
import { BillOfMaterial } from '../Mounting/BillOfMaterial';
import { calculateLgcyBom, LgcyBomResult } from '../../../../../utils/lgcy-bom.utils';
import { LgcyEnphaseBomResults } from '../../../../../types/lgcy-bom.types';
import { calculateBOM } from '../../../../../utils';
import { ModuleDimensions } from '../../../../../utils/bom.utils';
import { generateBomCsv, generateCadCsv, downloadCsv } from '../../../../../utils/bom-csv-export.utils';
import {
  parseBomCsv,
  applyImportedQuantities,
  matchNetsuiteItems,
  BomCsvImportError,
  ParsedBomCsv,
} from '../../../../../utils/bom-csv-import.utils';

interface BOMStepProps {
  isEnabled: boolean;
}

export const BOMStep: React.FC<BOMStepProps> = ({ isEnabled }) => {
  const dispatch = useAppDispatch();
  const bomData = useAppSelector(selectBomData);
  const packageItems = useAppSelector(selectLenderPackageItems);
  const clientNetsuiteItems = useAppSelector(selectClientNetsuiteItems);
  const isLgcyClient = useAppSelector(selectIsLgcyClient);
  const billOfMaterialData = useAppSelector(selectBillOfMaterialData);

  // LGCY BOM auto-calculated values from equipment/structures
  const autoTotalRows = useAppSelector(selectBomTotalRowsOfPanels);
  const autoStringsCircuits = useAppSelector(selectBomStringsCircuits);
  const autoMciS = useAppSelector(selectBomMciS);
  const totalModules = useAppSelector(selectBomTotalModules);
  const isEnphase = useAppSelector(selectBomIsEnphase);
  const totalInverters = useAppSelector(selectTotalInverters);
  const totalBatteries = useAppSelector(selectTotalBatteries);
  const totalOptimizersRsd = useAppSelector(selectTotalOptimizersRsd);
  const totalMicroinverters = useAppSelector(selectTotalMicroinverters);
  const offsetPercent = useAppSelector(selectLgcyBomOffset);
  const allSystemsModuleData = useAppSelector(selectAllSystemsModuleData);
  const structuresData = useAppSelector(selectStructuresData);

  // Standard BOM calculation
  const structure1Data = useAppSelector(selectStructure1Data);
  const structure2Data = useAppSelector(selectStructure2Data);
  const structure3Data = useAppSelector(selectStructure3Data);
  const safetyFactor = useAppSelector(selectSafetyFactor);

  // LGCY sub-state — uses selector with fallback for legacy saved CRs
  const lgcyData = useAppSelector(selectLgcyBomData);

  // LGCY BOM input values — read directly from Redux
  const totalRows = lgcyData.input_fields.total_rows;
  const stringsCircuits = lgcyData.input_fields.strings_circuits;
  const mciS = lgcyData.input_fields.mci_s;
  const arrays = lgcyData.input_fields.arrays;

  // Auto-populate input fields from equipment/structures — skips when manually overridden
  useEffect(() => {
    if (!isLgcyClient || lgcyData.input_fields.is_manual_override) return;
    dispatch(updateBomData({
      input_fields: {
        is_manual_override: false,
        total_rows: autoTotalRows,
        strings_circuits: autoStringsCircuits,
        mci_s: autoMciS,
        arrays: lgcyData.input_fields.arrays,
      },
    }));
  }, [autoTotalRows, autoStringsCircuits, autoMciS, isLgcyClient, dispatch]);

  const taskClient = useAppSelector(selectTaskClient);

  // LGCY client resolved from DB — used when taskClient.client_id is not available
  const [lgcyClient, setLgcyClient] = useState<{ id: string; name: string } | null>(null);

  // Client NetSuite catalog — shared by the Enphase auto-items sync and the Client Database section
  const lgcyClientId = lgcyClient?.id ?? taskClient.client_id ?? '';
  const netsuiteCatalog = useClientNetsuiteCatalog(isLgcyClient ? lgcyClientId : '');

  // Auto-attach Enphase Q Cable accessories with calculated quantities
  useEnphaseNetsuiteSync(netsuiteCatalog.items);

  // BOM mode switch confirmation
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  const [targetMode, setTargetMode] = useState<BomType | null>(null);

  const [lenders, setLenders] = useState<LenderResponse[]>([]);
  const [packages, setPackages] = useState<LenderPackageResponse[]>([]);
  const [isLoadingLenders, setIsLoadingLenders] = useState(false);
  const [isLoadingPackages, setIsLoadingPackages] = useState(false);

  // Full package items with enabled flag — local state for table toggle UX.
  // Redux lender_package_items only stores enabled items (stripped of disabled).
  const [localPackageItems, setLocalPackageItems] = useState<PackageItemState[]>(() => {
    // Initialize from Redux (all items in Redux are enabled when loaded from save)
    return packageItems.map(item => ({ ...item, enabled: true, bom_items: item.bom_items.map((b: any) => ({ ...b, enabled: true })) }));
  });

  // Standard BOM calculation
  const standardBomValues = useMemo(() => {
    const moduleDimensions: Partial<Record<string, { width: number; height: number }>> = {};
    (Object.keys(allSystemsModuleData) as Array<keyof typeof allSystemsModuleData>).forEach(key => {
      const mod = allSystemsModuleData[key];
      if (mod.width && mod.height) moduleDimensions[key] = { width: mod.width, height: mod.height };
    });
    return calculateBOM({
      structures: [
        {
          panel_rows_1: structure1Data?.attachments_rails?.panel_rows_1,
          panel_rows_2: structure1Data?.attachments_rails?.panel_rows_2,
          rail_1: structure1Data?.attachments_rails?.rail_1,
          rail_2: structure1Data?.attachments_rails?.rail_2,
        },
        {
          panel_rows_1: structure2Data?.attachments_rails?.panel_rows_1,
          panel_rows_2: structure2Data?.attachments_rails?.panel_rows_2,
          rail_1: structure2Data?.attachments_rails?.rail_1,
          rail_2: structure2Data?.attachments_rails?.rail_2,
        },
        {
          panel_rows_1: structure3Data?.attachments_rails?.panel_rows_1,
          panel_rows_2: structure3Data?.attachments_rails?.panel_rows_2,
          rail_1: structure3Data?.attachments_rails?.rail_1,
          rail_2: structure3Data?.attachments_rails?.rail_2,
        },
      ],
      moduleDimensions,
      safetyFactor,
    });
  }, [structure1Data, structure2Data, structure3Data, allSystemsModuleData, safetyFactor]);

  // Auto-update stored standard BOM values when not in manual override
  useEffect(() => {
    if (!isLgcyClient && !billOfMaterialData.is_manual_override) {
      dispatch(updateBillOfMaterialData({
        total_rails: standardBomValues.totalRails,
        total_splice_kits: standardBomValues.totalSpliceKits,
        total_mid_clamps: standardBomValues.totalMidClamps,
        total_end_clamps: standardBomValues.totalEndClamps,
        total_grounding_lugs: standardBomValues.totalGroundingLugs,
      }));
    }
  }, [standardBomValues, isLgcyClient, billOfMaterialData.is_manual_override, dispatch]);

  // When in LGCY mode, resolve the LGCY client from DB and fetch its associated lenders
  useEffect(() => {
    if (!isLgcyClient) {
      setLenders([]);
      setLgcyClient(null);
      return;
    }
    const resolveClientAndLenders = async () => {
      setIsLoadingLenders(true);
      try {
        const clients = await lenderPackagesService.getClients();
        const match = clients.find(c => c.name.toUpperCase().includes('LGCY'));
        if (match) {
          setLgcyClient({ id: match.id, name: match.name });
          const clientLenders = await lenderPackagesService.getLendersByClient(match.id);
          setLenders(clientLenders);
        }
      } catch (error) {
        console.error('Failed to resolve LGCY client or lenders:', error);
      } finally {
        setIsLoadingLenders(false);
      }
    };
    resolveClientAndLenders();
  }, [isLgcyClient]);

  // Fetch packages when lender changes
  useEffect(() => {
    if (!lgcyData.lender.id) { setPackages([]); return; }
    const fetchPackages = async () => {
      setIsLoadingPackages(true);
      try {
        const data = await lenderPackagesService.getPackagesByLender(lgcyData.lender.id);
        setPackages(data);
      } catch (error) {
        console.error('Failed to fetch packages:', error);
      } finally {
        setIsLoadingPackages(false);
      }
    };
    fetchPackages();
  }, [lgcyData.lender.id]);

  // When packages load from API and we have a saved package, rebuild localPackageItems:
  // 1. Map ALL items with auto-calculated quantities from BOM results
  // 2. Overlay Redux saved state: enabled flags + saved quantity overrides
  useEffect(() => {
    if (!lgcyData.package.id || packages.length === 0) return;
    const savedPkg = packages.find(p => p.id === lgcyData.package.id);
    if (!savedPkg) return;
    // Already populated from a fresh package select — skip merge
    if (localPackageItems.length > 0 && localPackageItems[0]?.id === savedPkg.items[0]?.id && localPackageItems.length === savedPkg.items.length) return;

    // Step 1: Map all items with auto-calculated quantities
    const r = lgcyData.bom_results;
    const mapped = mapPackageItems(savedPkg.items, {
      totalModules,
      totalInverters,
      totalBatteries,
      totalOptimizersRsd,
      totalMicroinverters,
      totalMciS: autoMciS,
      installationMethod: lgcyData.installation_method,
      estimatedAttachments: r.estimated_attachments,
      bomResults: {
        sumOfPanels: r.sum_of_panels,
        railsInTotal: r.rails_in_total,
        spliceKit: r.splice_kit,
        endCap: r.end_cap,
        midClamps: r.mid_clamps_total,
        endClamps: r.end_clamps_total,
        groundingLugs: r.grounding_lugs,
        wireClamp: r.universal_wire_clamp,
        wireRetentionClip: r.wire_retention_clip,
      },
    });

    // Step 2: Overlay Redux saved state (enabled flags + quantity overrides)
    const savedItemMap = new Map(packageItems.map(item => [item.id, item]));
    const merged = mapped.map(item => {
      const saved = savedItemMap.get(item.id);
      if (!saved) return item;

      const savedBomMap = new Map<string, { id: string; quantity: number }>(saved.bom_items.map((b: any) => [b.id, b]));
      return {
        ...item,
        enabled: true,
        bom_items: item.bom_items.map(bom => {
          const savedBom = savedBomMap.get(bom.id);
          if (!savedBom) return bom;
          return { ...bom, enabled: true, quantity: savedBom.quantity };
        }),
      };
    });

    setLocalPackageItems(merged);
    syncEnabledItemsToRedux(merged);
  }, [packages, lgcyData.package.id]);

  /** Sync only enabled items to Redux (strips disabled bom_items and empty parent items) */
  const syncEnabledItemsToRedux = useCallback((items: PackageItemState[]) => {
    const enabledOnly: PackageItemState[] = items
      .map(item => ({
        ...item,
        bom_items: item.bom_items.filter(b => b.enabled),
      }))
      .filter(item => item.enabled && item.bom_items.length > 0);
    dispatch(setPackageItems(enabledOnly));
  }, [dispatch]);

  const handleLenderSelect = useCallback((option: { id: string; label: string } | null) => {
    dispatch(updateBomData({ lender: { id: option?.id ?? '', name: option?.label ?? '' } }));
    setLocalPackageItems([]);
  }, [dispatch]);

  const handlePackageSelect = useCallback((option: { id: string; label: string } | null) => {
    if (!option) {
      dispatch(updateBomData({ package: { id: '', name: '', key: '', auto_enphase_accessories: false } }));
      dispatch(setPackageItems([]));
      setLocalPackageItems([]);
      return;
    }
    const selectedPkg = packages.find(p => p.id === option.id);
    dispatch(updateBomData({
      package: {
        id: option.id,
        name: selectedPkg?.name || option.label,
        key: selectedPkg?.package_key || '',
        auto_enphase_accessories: selectedPkg?.auto_enphase_accessories ?? false,
      },
    }));
    if (selectedPkg) {
      const r = lgcyData.bom_results;
      const items = mapPackageItems(selectedPkg.items, {
        totalModules,
        totalInverters,
        totalBatteries,
        totalOptimizersRsd,
        totalMicroinverters,
        totalMciS: autoMciS,
        installationMethod: lgcyData.installation_method,
        estimatedAttachments: r.estimated_attachments,
        bomResults: {
          sumOfPanels: r.sum_of_panels,
          railsInTotal: r.rails_in_total,
          spliceKit: r.splice_kit,
          endCap: r.end_cap,
          midClamps: r.mid_clamps_total,
          endClamps: r.end_clamps_total,
          groundingLugs: r.grounding_lugs,
          wireClamp: r.universal_wire_clamp,
          wireRetentionClip: r.wire_retention_clip,
        },
      });
      setLocalPackageItems(items);
      syncEnabledItemsToRedux(items);
    }
  }, [packages, dispatch, totalModules, totalInverters, totalBatteries, syncEnabledItemsToRedux]);

  const handleEssConfigSelect = useCallback((option: { id: string; label: string } | null) => {
    dispatch(updateBomData({ ess_config: (option?.id || '') as EssConfigValue | '' }));
  }, [dispatch]);

  const handleToggleAllBomItems = useCallback((itemId: string, enabled: boolean) => {
    setLocalPackageItems(prev => {
      const updated = prev.map(item =>
        item.id === itemId
          ? { ...item, enabled, bom_items: item.bom_items.map(b => ({ ...b, enabled })) }
          : item
      );
      syncEnabledItemsToRedux(updated);
      return updated;
    });
  }, [syncEnabledItemsToRedux]);

  const handleToggleBomItem = useCallback((itemId: string, bomItemId: string) => {
    setLocalPackageItems(prev => {
      const updated = prev.map(item => {
        if (item.id !== itemId) return item;
        const newBomItems = item.bom_items.map(b => b.id === bomItemId ? { ...b, enabled: !b.enabled } : b);
        const hasAnyEnabled = newBomItems.some(b => b.enabled);
        return { ...item, enabled: hasAnyEnabled, bom_items: newBomItems };
      });
      syncEnabledItemsToRedux(updated);
      return updated;
    });
  }, [syncEnabledItemsToRedux]);

  const handleBomItemQuantityChange = useCallback((itemId: string, bomItemId: string, quantity: number) => {
    setLocalPackageItems(prev => {
      const updated = prev.map(item =>
        item.id === itemId
          ? { ...item, bom_items: item.bom_items.map(b => b.id === bomItemId ? { ...b, quantity } : b) }
          : item
      );
      syncEnabledItemsToRedux(updated);
      return updated;
    });
  }, [syncEnabledItemsToRedux]);

  const handleRequestSwitch = useCallback((mode: BomType) => {
    setTargetMode(mode);
    setShowSwitchConfirm(true);
  }, []);

  const handleConfirmSwitch = useCallback(() => {
    if (!targetMode) return;
    dispatch(resetBom());
    dispatch(setBomType(targetMode));
    setLocalPackageItems([]);
    setShowSwitchConfirm(false);
    setTargetMode(null);
  }, [targetMode, dispatch]);


  // Enphase accessories are gated by both the inverter brand and the selected
  // package's flag. Legacy saved CRs lack the flag (undefined) — fall back to
  // brand-only behavior so their Q Cable items keep appearing.
  const enphaseAutoAddEnabled = isEnphase && (lgcyData.package.auto_enphase_accessories ?? true);

  // LGCY BOM calculation
  const lgcyBomResult: LgcyBomResult | null = useMemo(() => {
    if (!isLgcyClient || totalModules === 0) return null;
    const structures = (['structure_1', 'structure_2', 'structure_3'] as const).map(key => {
      const s = structuresData?.[key];
      return {
        panel_rows_1: s?.attachments_rails?.panel_rows_1,
        panel_rows_2: s?.attachments_rails?.panel_rows_2,
        rail_1: s?.attachments_rails?.rail_1,
        rail_2: s?.attachments_rails?.rail_2,
      };
    });
    const moduleDimensions: Partial<Record<string, ModuleDimensions>> = {};
    SYSTEM_KEYS.forEach(key => {
      const mod = allSystemsModuleData[key];
      if (mod?.width && mod?.height) moduleDimensions[key] = { width: mod.width, height: mod.height };
    });
    return calculateLgcyBom({
      bomInput: { structures, moduleDimensions, safetyFactor: 0 },
      totalRowsOfPanels: totalRows,
      arrays,
      stringsCircuits,
      mciS,
      totalModules,
      isEnphase: enphaseAutoAddEnabled,
      offsetPercent,
    });
  }, [isLgcyClient, totalRows, arrays, stringsCircuits, mciS, totalModules, enphaseAutoAddEnabled, offsetPercent, structuresData, allSystemsModuleData]);

  // Sync calculated BOM values to Redux — always keeps Redux in sync with calculation.
  // Manual-only fields (washer_lag) are preserved from current Redux state.
  // User edits via BomResultRow write directly to Redux and will differ from calculatedValue → "edited" badge.
  // On next calculation change, Redux gets overwritten with new calculated values (edit resets).
  useEffect(() => {
    if (!isLgcyClient || !lgcyBomResult) return;
    const current = lgcyData.bom_results;
    dispatch(updateLgcyBomResults({
      sum_of_panels: lgcyBomResult.sumOfPanels,
      rails_in_total: lgcyBomResult.railsInTotal,
      end_cap: lgcyBomResult.endCap,
      splice_kit: lgcyBomResult.spliceKit,
      mid_clamps_total: lgcyBomResult.midClampsTotal,
      end_clamps_total: lgcyBomResult.endClampsTotal,
      washer_lag: current.washer_lag,
      estimated_attachments: lgcyBomResult.estimatedAttachments,
      universal_wire_clamp: lgcyBomResult.universalWireClamp,
      wire_retention_clip: lgcyBomResult.wireRetentionClip,
      junction_box: lgcyBomResult.junctionBox,
      grounding_lugs: lgcyBomResult.groundingLugs,
      enphase: lgcyBomResult.enphase ? {
        cable_landscape: lgcyBomResult.enphase.qCableLandscape,
        cable_female_connector: lgcyBomResult.enphase.qCableFemaleConnector,
        cable_male_connector: lgcyBomResult.enphase.qCableMaleConnector,
        cable_sealing_cap: lgcyBomResult.enphase.qCableSealingCap,
        cable_terminator_cap: lgcyBomResult.enphase.qCableTerminatorCap,
      } : null,
    }));
  }, [lgcyBomResult, isLgcyClient, dispatch]);

  /** Recalculate lender package item quantities from current BOM results.
   * Called explicitly when user edits a BOM result field (left side).
   * Accepts an optional override so the new value is applied immediately
   * (Redux won't reflect the dispatch until the next render). */
  const recalculatePackageQuantities = useCallback((overrideKey?: string, overrideValue?: number) => {
    const r = lgcyData.bom_results;
    const v = (key: string, fallback: number): number =>
      overrideKey === key ? (overrideValue ?? fallback) : fallback;

    const input: PackageMapperInput = {
      totalModules,
      totalInverters,
      totalBatteries,
      totalOptimizersRsd,
      totalMicroinverters,
      totalMciS: autoMciS,
      installationMethod: lgcyData.installation_method,
      estimatedAttachments: v('estimated_attachments', r.estimated_attachments),
      bomResults: {
        sumOfPanels: v('sum_of_panels', r.sum_of_panels),
        railsInTotal: v('rails_in_total', r.rails_in_total),
        spliceKit: v('splice_kit', r.splice_kit),
        endCap: v('end_cap', r.end_cap),
        midClamps: v('mid_clamps_total', r.mid_clamps_total),
        endClamps: v('end_clamps_total', r.end_clamps_total),
        groundingLugs: v('grounding_lugs', r.grounding_lugs),
        wireClamp: v('universal_wire_clamp', r.universal_wire_clamp),
        wireRetentionClip: v('wire_retention_clip', r.wire_retention_clip),
      },
    };
    setLocalPackageItems(prev => {
      if (prev.length === 0) return prev;
      const updated = updatePackageItemQuantities(prev, input);
      syncEnabledItemsToRedux(updated);
      return updated;
    });
  }, [lgcyData.bom_results, totalModules, totalInverters, totalBatteries,
      totalOptimizersRsd, totalMicroinverters, autoMciS, lgcyData.installation_method,
      syncEnabledItemsToRedux]);

  // Shared CR auto-save — persists the current BOM (full Redux form_data) on demand
  const { autoSave } = useCrAutoSave();

  const handleSaveBom = useCallback(() => {
    autoSave({ successMessage: CR_AUTO_SAVE.BOM_SAVED_MESSAGE });
  }, [autoSave]);

  const handleExportCsv = useCallback(() => {
    const csvContent = generateBomCsv({
      packageItems,
      netsuiteItems: clientNetsuiteItems,
      lisNumber: lgcyData.lis_number,
      packageKey: lgcyData.package.key,
      essConfig: lgcyData.ess_config,
    });
    const filename = lgcyData.lis_number ? `BOM_${lgcyData.lis_number}.csv` : 'BOM_export.csv';
    downloadCsv(csvContent, filename);
    autoSave({ successMessage: CR_AUTO_SAVE.BOM_SAVED_MESSAGE });
  }, [packageItems, clientNetsuiteItems, lgcyData.lis_number, lgcyData.package.key, lgcyData.ess_config, autoSave]);

  const handleExportCad = useCallback(() => {
    const csvContent = generateCadCsv(packageItems, clientNetsuiteItems);
    const filename = lgcyData.lis_number ? `CAD_${lgcyData.lis_number}.csv` : 'CAD_export.csv';
    downloadCsv(csvContent, filename);
    autoSave({ successMessage: CR_AUTO_SAVE.BOM_SAVED_MESSAGE });
  }, [packageItems, clientNetsuiteItems, lgcyData.lis_number, autoSave]);

  // ── BOM CSV re-import (restore an unsaved BOM from a previous NetSuite export) ──
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);

  /** Builds the package mapper input from current equipment/BOM Redux state */
  const buildMapperInput = useCallback((): PackageMapperInput => {
    const r = lgcyData.bom_results;
    return {
      totalModules,
      totalInverters,
      totalBatteries,
      totalOptimizersRsd,
      totalMicroinverters,
      totalMciS: autoMciS,
      installationMethod: lgcyData.installation_method,
      estimatedAttachments: r.estimated_attachments,
      bomResults: {
        sumOfPanels: r.sum_of_panels,
        railsInTotal: r.rails_in_total,
        spliceKit: r.splice_kit,
        endCap: r.end_cap,
        midClamps: r.mid_clamps_total,
        endClamps: r.end_clamps_total,
        groundingLugs: r.grounding_lugs,
        wireClamp: r.universal_wire_clamp,
        wireRetentionClip: r.wire_retention_clip,
      },
    };
  }, [lgcyData.bom_results, lgcyData.installation_method, totalModules, totalInverters,
      totalBatteries, totalOptimizersRsd, totalMicroinverters, autoMciS]);

  /** Restores lender/package selection and shared fields (LIS#, ESS) from the CSV */
  const applyImportedSelection = useCallback((parsed: ParsedBomCsv, lookup: PackageLookupResult) => {
    dispatch(updateBomData({
      lender: {
        id: lookup.lender_id,
        name: lenders.find(l => l.id === lookup.lender_id)?.name ?? '',
      },
      package: {
        id: lookup.package.id,
        name: lookup.package.name,
        key: lookup.package.package_key,
        auto_enphase_accessories: lookup.package.auto_enphase_accessories,
      },
      lis_number: parsed.lisNumber,
      ess_config: parsed.essConfig,
    }));
  }, [dispatch, lenders]);

  /** Rebuilds package items and overlays CSV enabled flags + quantities */
  const applyImportedPackageItems = useCallback((parsed: ParsedBomCsv, pkg: LenderPackageResponse): Set<string> => {
    const mapped = mapPackageItems(pkg.items, buildMapperInput());
    const skuQuantities = new Map<string, number>(
      parsed.rows.map(row => [row.itemId, row.quantity]),
    );
    const { items, matchedSkus } = applyImportedQuantities(mapped, skuQuantities);
    setLocalPackageItems(items);
    syncEnabledItemsToRedux(items);
    return matchedSkus;
  }, [buildMapperInput, syncEnabledItemsToRedux]);

  /** Replaces the client NetSuite selection with CSV-matched items; returns unmatched Item IDs */
  const applyImportedNetsuiteItems = useCallback((parsed: ParsedBomCsv, matchedSkus: Set<string>): string[] => {
    const { matched, unmatchedItemIds } = matchNetsuiteItems({
      rows: parsed.rows,
      matchedSkus,
      catalogItems: netsuiteCatalog.items,
      isEnphaseProject: enphaseAutoAddEnabled && !!lgcyBomResult?.enphase,
    });
    dispatch(setNetsuiteItems(matched));
    return unmatchedItemIds;
  }, [netsuiteCatalog.items, dispatch, enphaseAutoAddEnabled, lgcyBomResult?.enphase]);

  const handleImportCsv = useCallback(async (file: File) => {
    setIsImporting(true);
    try {
      const parsed = parseBomCsv(await file.text());
      const lookup = await lenderPackagesService.findPackageByKey(lenders.map(l => l.id), parsed.packageKey);
      if (!lookup) {
        toast.error(BOM_CSV_IMPORT.PACKAGE_NOT_FOUND_MESSAGE);
        return;
      }
      setPackages(lookup.lender_packages);
      applyImportedSelection(parsed, lookup);
      const matchedSkus = applyImportedPackageItems(parsed, lookup.package);
      const unmatchedItemIds = applyImportedNetsuiteItems(parsed, matchedSkus);
      toast.success(`Imported ${parsed.rows.length - unmatchedItemIds.length} of ${parsed.rows.length} BOM items`);
      if (unmatchedItemIds.length > 0) {
        toast.error(`Unmatched Item IDs: ${unmatchedItemIds.join(', ')}`, {
          duration: BOM_CSV_IMPORT.UNMATCHED_TOAST_DURATION_MS,
        });
      }
    } catch (error) {
      console.error('BOM CSV import failed:', error);
      toast.error(error instanceof BomCsvImportError ? error.message : BOM_CSV_IMPORT.GENERIC_ERROR_MESSAGE);
    } finally {
      setIsImporting(false);
    }
  }, [lenders, applyImportedSelection, applyImportedPackageItems, applyImportedNetsuiteItems]);

  const handleImportFileSelected = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) await handleImportCsv(file);
  }, [handleImportCsv]);

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Footer count: enabled lender package items + enabled client NetSuite items
  const selectedItemsCount = packageItems.length + clientNetsuiteItems.filter(item => item.enabled).length;

  const hasLisAndEss = !!lgcyData.lis_number?.trim() && !!lgcyData.ess_config;
  const hasExportableItems = hasLisAndEss && packageItems.some(
    item => item.bom_items.some((b: any) => b.bom_sku)
  );
  const hasEnabledItems = hasLisAndEss && packageItems.length > 0;

  const lenderOptions = lenders.map(l => ({ id: l.id, label: l.name }));
  const packageOptions = packages.map(p => ({ id: p.id, label: p.name }));
  const essConfigOptions = ESS_CONFIG_OPTIONS.map(o => ({ id: o.value, label: o.label }));
  const installMethodOptions = INSTALLATION_METHOD_OPTIONS.map(o => ({ id: o.value, label: o.label }));

  const selectedLender = lgcyData.lender.id ? { id: lgcyData.lender.id, label: lgcyData.lender.name } : null;
  const selectedPackage = lgcyData.package.id ? { id: lgcyData.package.id, label: lgcyData.package.name } : null;
  const selectedEssConfig = useMemo(() => {
    const opt = ESS_CONFIG_OPTIONS.find(o => o.value === lgcyData.ess_config);
    return opt ? { id: opt.value, label: opt.label } : null;
  }, [lgcyData.ess_config]);
  const selectedInstallMethod = useMemo(() => {
    const opt = INSTALLATION_METHOD_OPTIONS.find(o => o.value === lgcyData.installation_method);
    return opt ? { id: opt.value, label: opt.label } : null;
  }, [lgcyData.installation_method]);

  const handleInstallMethodSelect = useCallback((option: { id: string; label: string } | null) => {
    if (option) dispatch(updateBomData({ installation_method: option.id as typeof lgcyData.installation_method }));
  }, [dispatch]);


  // ── Shared switch confirmation modal ──────────────────────────────────────
  const switchModal = showSwitchConfirm && (
    <ConfirmationModal
      isOpen
      type={ConfirmationType.WARNING}
      title={`Switch to ${targetMode === 'lgcy' ? 'LGCY' : 'Standard'} BOM?`}
      message="All current BOM data will be cleared. This cannot be undone."
      confirmButtonText="Switch & Reset"
      onConfirm={handleConfirmSwitch}
      onClose={() => setShowSwitchConfirm(false)}
    />
  );

  // ── Standard BOM view (non-LGCY) ──────────────────────────────────────────
  if (!isLgcyClient) {
    return (
      <div className="flex-1 flex flex-col min-h-0">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 flex flex-col flex-1 min-h-0 overflow-hidden">
          <BomModeHeader detectedClient={taskClient.client} isLgcyActive={false} isEnabled={isEnabled} onRequestSwitch={handleRequestSwitch} />
          <div className="flex-1 overflow-y-auto p-5">
            <BillOfMaterial isEnabled={isEnabled} calculatedValues={standardBomValues} storedValues={billOfMaterialData} onUpdate={(data) => dispatch(updateBillOfMaterialData(data))} />
          </div>
        </div>
        {switchModal}
      </div>
    );
  }

  // ── LGCY BOM view — single container with dividers ────────────────────────
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 flex flex-col flex-1 min-h-0 overflow-hidden">

        {/* ─── Section 1: Header ─── */}
        <BomModeHeader
          detectedClient={taskClient.client}
          isLgcyActive
          isEnabled={isEnabled}
          onRequestSwitch={handleRequestSwitch}
          onImportCsv={handleImportClick}
          isImportDisabled={!isEnabled || isImporting || isLoadingLenders || netsuiteCatalog.isLoading}
          secondaryActions={
            <button type="button" onClick={handleSaveBom} disabled={!isEnabled}
              className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-emerald-600 to-teal-600 px-3 py-1.5 text-[10px] 3xl:text-xs font-semibold text-white shadow-sm shadow-emerald-500/20 hover:from-emerald-500 hover:to-teal-500 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>
              Save BOM
            </button>
          }
          actions={
            <>
              <button type="button" onClick={handleExportCad} disabled={!hasEnabledItems} title={!hasLisAndEss ? 'LIS# and ESS Config required' : undefined}
                className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-[10px] 3xl:text-xs font-semibold text-white shadow-sm hover:bg-red-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                Export to CAD
              </button>
              <button type="button" onClick={handleExportCsv} disabled={!hasExportableItems} title={!hasLisAndEss ? 'LIS# and ESS Config required' : undefined}
                className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-[10px] 3xl:text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                Export to NetSuite
              </button>
            </>
          }
        />
        <input ref={fileInputRef} type="file" accept={BOM_CSV_IMPORT.FILE_ACCEPT} className="hidden" onChange={handleImportFileSelected} />

        {/* ─── Section 2: Config strip ─── */}
        <div className="shrink-0 px-5 py-3.5 border-b border-slate-200/60">
          <div className="grid gap-3 items-end" style={{ gridTemplateColumns: '15% 15% 15% auto 15% 1fr' }}>
            <ConfigField label="Installation">
              <SearchDropdown options={installMethodOptions} onSelect={handleInstallMethodSelect} value={selectedInstallMethod} placeholder="Select method" disabled={!isEnabled} enableArrowNavigation inputClassName="!py-1.5 !px-2.5 !text-[11px] !rounded-lg !border-slate-200 !bg-slate-50/50 focus:!bg-white" />
            </ConfigField>
            <ConfigField label="LIS#">
              <input type="text" value={lgcyData.lis_number} onChange={(e) => dispatch(updateBomData({ lis_number: e.target.value }))} placeholder="Enter LIS#" disabled={!isEnabled}
                className="w-full rounded-lg border border-slate-200 bg-slate-50/50 py-1.5 px-2.5 text-[11px] text-slate-700 placeholder-slate-300 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 focus:bg-white outline-none transition-all disabled:bg-slate-100 disabled:text-slate-400"
              />
            </ConfigField>
            <ConfigField label="ESS Config">
              <SearchDropdown options={essConfigOptions} onSelect={handleEssConfigSelect} value={selectedEssConfig} placeholder="Select ESS" disabled={!isEnabled} enableArrowNavigation inputClassName="!py-1.5 !px-2.5 !text-[11px] !rounded-lg !border-slate-200 !bg-slate-50/50 focus:!bg-white" />
            </ConfigField>
            <div className="flex items-center justify-center pb-1"><div className="w-px h-7 bg-slate-200" /></div>
            <ConfigField label="Lender">
              <SearchDropdown options={lenderOptions} onSelect={handleLenderSelect} value={selectedLender} placeholder={isLoadingLenders ? 'Loading...' : 'Select Lender'} isLoading={isLoadingLenders} disabled={!isEnabled} enableArrowNavigation inputClassName="!py-1.5 !px-2.5 !text-[11px] !rounded-lg !border-slate-200 !bg-slate-50/50 focus:!bg-white" />
            </ConfigField>
            <ConfigField label="Package">
              <SearchDropdown options={packageOptions} onSelect={handlePackageSelect} value={selectedPackage} placeholder={isLoadingPackages ? 'Loading...' : !lgcyData.lender.id ? 'Select a lender first' : 'Select Package'} isLoading={isLoadingPackages} disabled={!isEnabled || !lgcyData.lender.id} enableArrowNavigation inputClassName="!py-1.5 !px-2.5 !text-[11px] !rounded-lg !border-slate-200 !bg-slate-50/50 focus:!bg-white" />
            </ConfigField>
          </div>
        </div>

        {/* ─── Section 3: Two-column body ─── */}
        <div className="flex flex-1 min-h-0">

          {/* Left — BOM Inputs + Results */}
          <div className="w-[260px] 2xl:w-[290px] 3xl:w-[320px] flex flex-col shrink-0 border-r border-slate-200/60">
            {/* Inputs */}
            <div className="px-4 py-3.5 border-b border-slate-100 bg-amber-50/30 shrink-0">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-5 h-5 rounded-md bg-amber-500 flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                </div>
                <span className="text-[10px] 3xl:text-xs font-bold text-amber-800 uppercase tracking-wider">Inputs</span>
              </div>
              <div className="grid grid-cols-4 gap-1">
                <BomInputField label="Panel Rows" value={totalRows} isEnabled={isEnabled} onChange={(val) => dispatch(updateBomData({ input_fields: { ...lgcyData.input_fields, is_manual_override: true, total_rows: Math.max(0, val) } }))} />
                <BomInputField label="MCI-S" value={mciS} isEnabled={isEnabled} onChange={(val) => dispatch(updateBomData({ input_fields: { ...lgcyData.input_fields, is_manual_override: true, mci_s: Math.max(0, val) } }))} />
                <BomInputField label="Strings" value={stringsCircuits} isEnabled={isEnabled} onChange={(val) => dispatch(updateBomData({ input_fields: { ...lgcyData.input_fields, is_manual_override: true, strings_circuits: Math.max(0, val) } }))} />
                <BomInputField label="Arrays" value={arrays} isEnabled={isEnabled} highlight={arrays === 0} onChange={(val) => dispatch(updateBomData({ input_fields: { ...lgcyData.input_fields, is_manual_override: true, arrays: Math.max(0, val) } }))} />
              </div>
            </div>
            {/* Results */}
            <div className="flex-1 overflow-y-auto">
              {!lgcyBomResult ? (
                <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                  <svg className="w-8 h-8 text-slate-200 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p className="text-xs text-slate-400">Configure equipment to see results</p>
                </div>
              ) : (
                <BomResultsTable result={lgcyBomResult} isEnabled={isEnabled} onResultChange={recalculatePackageQuantities} />
              )}
            </div>
          </div>

          {/* Right — Package Items */}
          <div className="flex-1 flex flex-col min-w-0">
            {(!lgcyData.lender.id || !lgcyData.package.id) ? (
              <div className="flex flex-col items-center justify-center flex-1 p-8">
                <svg className="w-10 h-10 text-orange-200 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <p className="text-sm font-medium text-slate-400">Select a lender and package</p>
                <p className="text-xs text-slate-300 mt-1">to view and manage items</p>
              </div>
            ) : isLoadingPackages || (lgcyData.package.id && localPackageItems.length === 0) ? (
              <div className="flex flex-col items-center justify-center flex-1 p-8">
                <svg className="w-6 h-6 text-amber-400 animate-spin mb-3" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <p className="text-xs text-slate-400">Loading package items...</p>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {localPackageItems.length > 0 && (
                  <LenderPackagesTable items={localPackageItems} isEnabled={isEnabled} packageKey={lgcyData.package.key} onToggleAllBomItems={handleToggleAllBomItems} onToggleBomItem={handleToggleBomItem} onBomItemQuantityChange={handleBomItemQuantityChange} />
                )}
                {enphaseAutoAddEnabled && lgcyBomResult?.enphase && (
                  <EnphaseItemsSection catalogItems={netsuiteCatalog.items} isEnabled={isEnabled} />
                )}
                <ClientPackagesSection clientId={lgcyClientId} clientName={lgcyClient?.name ?? taskClient.client_name ?? ''} isEnabled={isEnabled} catalogItems={netsuiteCatalog.items} isCatalogLoading={netsuiteCatalog.isLoading} />
                <div className="flex items-center pt-3 border-t border-slate-100">
                  <p className="text-[10px] text-slate-400">{selectedItemsCount} item{selectedItemsCount !== 1 ? 's' : ''} selected</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {switchModal}
    </div>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

const ConfigField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">{label}</label>
    {children}
  </div>
);

interface BomModeHeaderProps {
  detectedClient: string | null;
  isLgcyActive: boolean;
  isEnabled: boolean;
  onRequestSwitch?: (mode: BomType) => void;
  onImportCsv?: () => void;
  isImportDisabled?: boolean;
  /** Primary action buttons rendered in the middle of the header (e.g. exports) */
  actions?: React.ReactNode;
  /** Secondary link-styled actions rendered on the right, before the import link */
  secondaryActions?: React.ReactNode;
}

const BomModeHeader: React.FC<BomModeHeaderProps> = ({
  detectedClient, isLgcyActive, isEnabled, onRequestSwitch, onImportCsv, isImportDisabled, actions, secondaryActions,
}) => {
  const switchTarget = isLgcyActive ? 'standard' : 'lgcy';
  const switchLabel = isLgcyActive ? 'Standard' : 'LGCY';

  return (
    <div className="shrink-0 flex items-center justify-between px-5 py-3.5 border-b border-slate-200/60">
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 shadow-sm ${isLgcyActive ? 'bg-gradient-to-br from-amber-500 to-orange-600' : 'bg-gradient-to-br from-slate-500 to-slate-700'}`}>
          <svg className="w-4.5 h-4.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-slate-800 leading-tight">Bill of Materials</h3>
          <div className="flex items-center gap-2 mt-1">
            {detectedClient && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] 3xl:text-[11px] font-medium text-slate-500">
                <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                {detectedClient}
              </span>
            )}
            {isLgcyActive ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] 3xl:text-[11px] font-bold text-amber-700">
                <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" /></svg>
                LGCY
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] 3xl:text-[11px] font-bold text-emerald-600">
                <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Standard
              </span>
            )}
            {onRequestSwitch && (
              <button type="button" onClick={() => onRequestSwitch(switchTarget)} disabled={!isEnabled}
                className="text-[10px] 3xl:text-[11px] font-medium text-slate-400 underline underline-offset-2 hover:text-indigo-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                Switch to {switchLabel}
              </button>
            )}
          </div>
        </div>
      </div>
      {actions && (
        <div className="shrink-0 flex items-center gap-2">
          {actions}
        </div>
      )}
      {(secondaryActions || onImportCsv) && (
        <div className="shrink-0 flex items-center gap-3">
          {onImportCsv && (
            <button type="button" onClick={onImportCsv} disabled={isImportDisabled}
              className="inline-flex items-center gap-1 text-[10px] 3xl:text-[11px] font-medium text-orange-500 underline underline-offset-2 hover:text-orange-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
              Import CSV
            </button>
          )}
          {secondaryActions}
        </div>
      )}
    </div>
  );
};

interface BomInputFieldProps {
  label: string;
  value: number;
  isEnabled: boolean;
  onChange: (value: number) => void;
  highlight?: boolean;
}

const BomInputField: React.FC<BomInputFieldProps> = ({
  label, value, isEnabled, onChange, highlight,
}) => (
  <div className="space-y-0.5">
    <label className="text-[8px] 3xl:text-[9px] font-semibold uppercase tracking-wider text-slate-400 block">{label}</label>
    <NumericInput
      value={value}
      onChange={onChange}
      disabled={!isEnabled}
      className={`w-full rounded-md border px-2 py-1 text-[11px] font-semibold text-slate-700 focus:ring-1 outline-none transition-all disabled:bg-slate-50 disabled:text-slate-400 ${
        highlight
          ? 'border-red-300 bg-red-50/50 text-red-600 focus:border-red-400 focus:ring-red-200'
          : 'border-slate-200 bg-slate-50/50 focus:border-amber-400 focus:ring-amber-200 focus:bg-white'
      }`}
    />
  </div>
);

interface BomResultsTableProps {
  result: LgcyBomResult;
  isEnabled: boolean;
  onResultChange?: (reduxKey: string, value: number) => void;
}

const BomResultsTable: React.FC<BomResultsTableProps> = ({ result, isEnabled, onResultChange }) => {
  const dispatch = useAppDispatch();
  const lgcyResults = useAppSelector(selectLgcyBomData).bom_results;

  /** Map from renderRow key to the Redux lgcy bom_results field name */
  const KEY_MAP: Record<string, keyof typeof lgcyResults> = {
    sumOfPanels: 'sum_of_panels',
    railsInTotal: 'rails_in_total',
    endCap: 'end_cap',
    spliceKit: 'splice_kit',
    midClampsTotal: 'mid_clamps_total',
    endClampsTotal: 'end_clamps_total',
    washerLag11632: 'washer_lag',
    estimatedAttachments: 'estimated_attachments',
    universalWireClamp: 'universal_wire_clamp',
    wireRetentionClip: 'wire_retention_clip',
    junctionBox: 'junction_box',
    groundingLugs: 'grounding_lugs',
  };

  /** Map from renderRow key to the nested enphase field name */
  const ENPHASE_KEY_MAP: Record<string, keyof LgcyEnphaseBomResults> = {
    qCableLandscape: 'cable_landscape',
    qCableFemaleConnector: 'cable_female_connector',
    qCableMaleConnector: 'cable_male_connector',
    qCableSealingCap: 'cable_sealing_cap',
    qCableTerminatorCap: 'cable_terminator_cap',
  };

  const handleChange = (key: string, value: string): void => {
    const numValue = Math.max(0, parseInt(value) || 0);

    const reduxKey = KEY_MAP[key];
    if (reduxKey) {
      dispatch(setLgcyBomResultField({ key: reduxKey, value: numValue }));
      onResultChange?.(reduxKey, numValue);
      return;
    }

    const enphaseKey = ENPHASE_KEY_MAP[key];
    if (enphaseKey && lgcyResults.enphase) {
      dispatch(setLgcyBomResultField({ key: `enphase.${enphaseKey}`, value: numValue }));
    }
  };

  const renderRow = (key: string, label: string, calculated: number, options?: { accent?: boolean; manual?: boolean; minValue?: number }): React.ReactElement => {
    const reduxKey = KEY_MAP[key];
    const enphaseKey = ENPHASE_KEY_MAP[key];
    const reduxVal = reduxKey
      ? (lgcyResults[reduxKey] as number)
      : enphaseKey && lgcyResults.enphase
        ? lgcyResults.enphase[enphaseKey]
        : calculated;
    const val = reduxVal ?? calculated;
    return (
      <BomResultRow
        label={label}
        value={val}
        calculatedValue={calculated}
        isManual={!!options?.manual}
        accent={!!options?.accent}
        isEnabled={isEnabled}
        minValue={options?.minValue}
        onChange={(v: number) => handleChange(key, String(v))}
      />
    );
  };

  return (
    <div className="divide-y divide-slate-100/80">
      {/* Section header */}
      <div className="flex items-center gap-1.5 px-3 3xl:px-4 py-2 3xl:py-2.5 bg-emerald-50/50">
        <div className="w-4 h-4 3xl:w-5 3xl:h-5 rounded-md bg-emerald-500 flex items-center justify-center">
          <svg className="w-2.5 h-2.5 3xl:w-3 3xl:h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        </div>
        <span className="text-[9px] 3xl:text-[10px] font-bold text-emerald-700 uppercase tracking-wider">BOM</span>
        <span className="text-[9px] 3xl:text-[10px] text-slate-400 ml-auto tabular-nums">{result.sumOfPanels} panels</span>
      </div>

      {renderRow('sumOfPanels', 'Total Panels', result.sumOfPanels)}
      {renderRow('railsInTotal', 'Rails in Total', result.railsInTotal)}
      {renderRow('endCap', 'End Cap', result.endCap)}
      {renderRow('spliceKit', 'Splice Kit', result.spliceKit, { minValue: 2 })}
      {renderRow('midClampsTotal', 'Mid Clamps', result.midClampsTotal)}
      {renderRow('endClampsTotal', 'End Clamps', result.endClampsTotal)}
      {renderRow('groundingLugs', 'Grounding Lugs', result.groundingLugs)}
      {renderRow('washerLag11632', 'Washer-Lag', result.washerLag11632, { manual: true })}
      {renderRow('estimatedAttachments', 'Est. Attachments', result.estimatedAttachments)}
      {renderRow('universalWireClamp', 'Wire Clamp', result.universalWireClamp)}
      {renderRow('wireRetentionClip', 'Wire Retention Clip', result.wireRetentionClip)}
      {renderRow('junctionBox', 'Junction Box', result.junctionBox)}

      {result.enphase && (
        <>
          <div className="flex items-center gap-1.5 px-3 3xl:px-4 py-2 3xl:py-2.5 bg-blue-50/50">
            <div className="w-4 h-4 3xl:w-5 3xl:h-5 rounded-md bg-blue-500 flex items-center justify-center">
              <svg className="w-2.5 h-2.5 3xl:w-3 3xl:h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
            </div>
            <span className="text-[9px] 3xl:text-[10px] font-bold text-blue-700 uppercase tracking-wider">Enphase</span>
          </div>
          {renderRow('qCableLandscape', 'Q Cable 60 Cell', result.enphase.qCableLandscape, { accent: true })}
          {renderRow('qCableFemaleConnector', 'Q Cable Female', result.enphase.qCableFemaleConnector, { accent: true })}
          {renderRow('qCableMaleConnector', 'Q Cable Male', result.enphase.qCableMaleConnector, { accent: true })}
          {renderRow('qCableSealingCap', 'Sealing Cap', result.enphase.qCableSealingCap, { accent: true })}
          {renderRow('qCableTerminatorCap', 'Terminator Cap', result.enphase.qCableTerminatorCap, { accent: true })}
        </>
      )}
    </div>
  );
};

interface BomResultRowProps {
  label: string;
  value: number;
  calculatedValue: number;
  isManual: boolean;
  accent: boolean;
  isEnabled: boolean;
  minValue?: number;
  onChange: (value: number) => void;
}

const BomResultRow: React.FC<BomResultRowProps> = ({ label, value, calculatedValue, isManual, accent, isEnabled, minValue = 0, onChange }) => {
  const isManualEmpty = isManual && value === 0;
  const isOverridden = !isManual && value !== calculatedValue;

  return (
    <div className={`group flex items-center justify-between px-3 3xl:px-4 py-1.5 3xl:py-2 transition-colors ${isManualEmpty ? 'bg-red-50/40' : isOverridden ? 'bg-amber-50/30' : accent ? 'hover:bg-blue-50/30' : 'hover:bg-slate-50'}`}>
      <span className={`text-[10px] 3xl:text-xs flex-1 mr-1.5 truncate ${isManualEmpty ? 'text-red-500 font-semibold' : isOverridden ? 'text-amber-700 font-medium' : 'text-slate-600'}`}>{label}</span>
      <div className="flex items-center gap-1 shrink-0">
        {isOverridden && (
          <span className="text-[6px] 3xl:text-[7px] font-semibold uppercase text-amber-500">edited</span>
        )}
        <NumericInput
          value={value}
          onChange={onChange}
          min={minValue}
          disabled={!isEnabled}
          className={`w-10 3xl:w-12 text-center text-[10px] 3xl:text-xs font-bold rounded-md tabular-nums outline-none transition-all border disabled:cursor-default py-0.5 3xl:py-1 px-0.5 3xl:px-1 ${
            isManualEmpty
              ? 'bg-red-50 border-red-300 text-red-500 focus:border-red-400 focus:ring-1 focus:ring-red-200'
              : isOverridden
                ? 'bg-amber-50 border-amber-300 text-amber-700 focus:border-amber-400 focus:ring-1 focus:ring-amber-200'
                : 'bg-slate-50 border-slate-200 text-slate-700 hover:border-slate-300 focus:border-blue-400 focus:ring-1 focus:ring-blue-200 focus:bg-white disabled:text-slate-400'
          }`}
        />
      </div>
    </div>
  );
};
